import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const PAGE_SIZE = 30;
const STATUSES = ["ALL", "PENDING", "APPROVED", "REJECTED"];

export default function ModerationComments() {
  const [status, setStatus] = useState("ALL");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      localStorage.getItem("theme") || "light"
    );
  }, []);

  useEffect(() => {
    let cancel = false;
    const controller = new AbortController();
    async function fetchData() {
      setLoading(true);
      setErr("");
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(PAGE_SIZE),
        });
        if (status !== "ALL") params.set("status", status);
        
        const res = await axios.get(`/api/community/comments?${params.toString()}`, {
          withCredentials: true,
          signal: controller.signal,
        });
        const data = res?.data || {};
        if (!cancel) {
          setItems(data.items || []);
          setTotal(data.total || 0);
        }
      } catch (e) {
        if (!cancel) {
          setErr("Failed to load comments");
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    fetchData();
    return () => { cancel = true; controller.abort(); };
  }, [status, page]);

  async function updateStatus(id, newStatus) {
    try {
      await axios.patch(`/api/community/comments/${id}/status`, { status: newStatus }, { withCredentials: true });
      setItems((arr) => arr.map((it) => (it._id === id ? { ...it, status: newStatus } : it)));
      const msg = newStatus === "APPROVED" ? "Approved comment"
        : newStatus === "REJECTED" ? "Rejected comment"
        : "Updated comment";
      toast.success(msg);
    } catch {}
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const hasActiveFilters = useMemo(() => {
    return status !== "ALL";
  }, [status]);

  const clearFilters = () => {
    setStatus("ALL");
    setPage(1);
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Community ▸ Comments Moderation</h2>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <select 
          value={status} 
          onChange={(e) => { setPage(1); setStatus(e.target.value); }} 
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          <option value="ALL">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
          >
            Clear Filters
          </button>
        )}
      </div>

      {err && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          {err}
        </div>
      )}

      <div className="mt-4 rounded-xl border bg-white overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2">Post</th>
              <th className="px-3 py-2">Content</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-3 py-6 text-center" colSpan={5}>Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td className="px-3 py-6 text-center" colSpan={5}>No items</td></tr>
            ) : (
              items.map((it) => (
                <tr key={it._id} className="border-t">
                  <td className="px-3 py-2 text-xs truncate max-w-[280px]">{it.postTitle || it.postId}</td>
                  <td className="px-3 py-2 truncate max-w-[360px]">
                    <div 
                      dangerouslySetInnerHTML={{ __html: it.content }}
                      className="truncate"
                    />
                  </td>
                  <td className="px-3 py-2 text-xs">{it.status}</td>
                  <td className="px-3 py-2 text-xs">{new Date(it.createdAt).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 justify-end">
                      <select
                        value={it.status}
                        onChange={(e) => updateStatus(it._id, e.target.value)}
                        className="rounded border border-gray-200 px-2 py-1 text-xs bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                      >
                        <option value="PENDING">Pending</option>
                        <option value="APPROVED">Approved</option>
                        <option value="REJECTED">Rejected</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button className="rounded border px-3 py-1.5" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
          <span>Page {page} / {totalPages}</span>
          <button className="rounded border px-3 py-1.5" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
        </div>
      )}
    </div>
  );
}


